import React from 'react'
import { Box, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import useProductCurrency from '../../../../../../../hooks/useProductCurrency'
import { formatDateLocale } from '../../../../../../../lib/utils.es6'

export const useCoverLinkStyles = makeStyles(() => ({
  mainCard: {
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
    marginBottom: '20px',
    background: '#FFFFFF',
    border: '1px solid #D9D9D9',
    maxWidth: '800px',
    margin: '0 auto'
  },
  headerSection: {
    background: 'linear-gradient(135deg, #032a37 0%, #173E4B 100%)',
    color: 'white',
    padding: '20px 30px',
    borderRadius: '8px 8px 0 0',
    borderBottom: '2px solid #e5e5e5'
  },
  policyIcon: {
    background: 'rgba(255,255,255,0.2)',
    borderRadius: '50%',
    width: '50px',
    height: '50px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  policyTitle: {
    fontFamily: 'Lato',
    fontWeight: '600',
    color: '#009652',
    fontSize: '18px',
    marginBottom: '5px'
  },
  policyNumber: {
    fontFamily: 'Open Sans',
    fontSize: '12px',
    color: 'white',
    marginBottom: '8px'
  },
  statusBadge: {
    display: 'inline-block',
    fontSize: '10px',
    padding: '4px 12px',
    borderRadius: '20px',
    fontWeight: 'bold',
    fontFamily: 'Open Sans'
  },
  statusSuccess: {
    backgroundColor: '#d4edda',
    color: '#155724'
  },
  statusWarning: {
    backgroundColor: '#fff3cd',
    color: '#856404'
  },
  statusError: {
    backgroundColor: '#f8d7da',
    color: '#721c24'
  },
  statusDefault: {
    backgroundColor: '#e2e3e5',
    color: '#383d41'
  },
  box: {
    border: '1px solid #e5e5e5',
    borderRadius: '8px',
    padding: '20px',
    backgroundColor: '#fafafa',
    marginBottom: '20px'
  },
  boxTitle: {
    fontFamily: 'Lato',
    fontSize: '16px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '15px',
    marginTop: '0'
  },
  memberItem: {
    marginBottom: '15px',
    paddingBottom: '10px',
    borderBottom: '1px solid #e5e5e5',
    '&:last-child': {
      borderBottom: 'none',
      marginBottom: '0'
    }
  },
  premiumRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '8px'
  },
  premiumTotal: {
    borderTop: '1px solid #ccc',
    paddingTop: '10px',
    marginTop: '15px',
    fontWeight: 'bold'
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px'
  },
  scopeList: {
    listStyleType: 'disc',
    marginLeft: '20px',
    marginTop: '10px',
    '& li': {
      marginBottom: '5px',
      fontFamily: 'Open Sans',
      fontSize: '12px',
      color: '#333'
    }
  },
  labelText: {
    fontFamily: 'Open Sans',
    color: '#A9A9A9',
    fontSize: '12px',
    fontWeight: '400',
    textTransform: 'uppercase',
    letterSpacing: '0.005em',
    marginBottom: '4px',
    fontStyle: 'normal',
    lineHeight: '16px'
  },
  valueText: {
    fontFamily: 'Lato',
    color: '#000000',
    fontSize: '12px',
    fontWeight: '500',
    wordBreak: 'break-word',
    fontStyle: 'normal',
    lineHeight: '20px',
    letterSpacing: '0.001em',
    margin: '5px 0'
  },
  strongText: {
    fontFamily: 'Lato',
    fontWeight: '600',
    color: '#000000',
    fontSize: '12px'
  },
  actionButton: {
    background: 'rgba(255,255,255,0.1)',
    '&:hover': {
      background: 'rgba(255,255,255,0.2)'
    }
  },
  principalInfo: {
    textAlign: 'right',
    color: 'white'
  },
  contentWrapper: {
    padding: '30px'
  }
}))

export const getStatusLabel = function(status) {
  const formatActual = function(s) {
    return s.replace(/_/g, ' ')
  }
  switch (status) {
    case 'Unpaid':
    case 'Un_confirmed':
      return `OPEN (${formatActual(status)})`
    case 'Full_Coverage':
    case 'In_Arrears':
      return `ISSUED (${formatActual(status)})`
    case 'Cancelled':
      return 'Cancelled'
    case 'Rejected':
      return 'Rejected'
    case 'Non_Payment':
      return 'Non Payment'
    case 'Not_Taken_Up':
      return 'Not Taken Up'
    case 'Discarded':
      return 'Discarded'
    case 'Expired':
      return 'Expired'
    case 'Pending':
      return 'Pending'
    default:
      return status
  }
}

export const getStatusColor = function(status) {
  switch (status) {
    case 'Full_Coverage':
    case 'In_Arrears':
      return 'success'
    case 'Unpaid':
    case 'Un_confirmed':
      return 'warning'
    case 'Cancelled':
    case 'Rejected':
    case 'Discarded':
      return 'error'
    default:
      return 'default'
  }
}

const CHILD_RELATIONSHIPS = [
  'child',
  'step_son',
  'step_daughter',
  'daughter',
  'son',
  'nephew',
  'niece',
  'grandson',
  'granddaughter',
  'grandchild'
]

export const getPrincipalMember = quotation => {
  if (quotation && quotation.policy_schedule_entries) {
    return quotation.policy_schedule_entries.find(
      entry => entry.dependant && entry.dependant.relationship === 'Principal'
    )
  }
  return null
}

/**
 * Shared presentational detail for a single CoverLink quotation.
 *
 * `quotation` provides the priced schedule (products / schedule entries /
 * premiums). `meta` provides the onboarding metadata that lives at the policy
 * level (company/next-of-kin/employment/bank details, product_code, status,
 * dates). For the policy-processing single-policy view, meta = the loaded
 * policy and quotation = policy.quotation. For the customer quotation-history
 * list, meta = quotation = the quotation object itself (metadata not captured
 * on a quotation simply renders as "N/A").
 */
export default function CoverLinkQuotationDetail({ quotation, meta }) {
  const classes = useCoverLinkStyles()

  const source = meta || quotation || {}
  const currencyCode = useProductCurrency(
    (quotation && quotation.product_code) || source.product_code
  )

  const isLegalPolicy = source.product_code === 'legal_cover'
  const isMedicalPolicy = source.product_code === 'Medical_Cash_Plan'
  const isDomesticPolicy =
    source.product_code === 'domestic_cover' ||
    (source.policy_number && source.policy_number.startsWith('CIC-DOM'))

  const renderSelectedProduct = () => {
    const legalDetails = quotation && quotation.additional_details

    return (
      <Box className={classes.box}>
        <Typography className={classes.boxTitle}>Selected Product</Typography>
        {isLegalPolicy ? (
          <div>
            <Typography className={classes.valueText}>
              <span className={classes.strongText}>
                {source.product_name} – {legalDetails && legalDetails.tier} Tier
              </span>
            </Typography>
            <Typography className={classes.valueText}>
              Sum Assured: {currencyCode}
              {legalDetails && legalDetails.sum_assured
                ? legalDetails.sum_assured.toLocaleString()
                : 'N/A'}
            </Typography>
            <Typography className={classes.valueText}>
              Cover Type:{' '}
              {legalDetails && legalDetails.cover_type
                ? legalDetails.cover_type
                : 'N/A'}
            </Typography>
            <Typography className={classes.valueText}>
              Monthly Premium: {currencyCode}
              {quotation && quotation.total_premium_amount_in_cents
                ? (
                    quotation.total_premium_amount_in_cents / 100
                  ).toLocaleString()
                : 'N/A'}
            </Typography>
          </div>
        ) : (
          <div>
            <Typography className={classes.valueText}>
              <span className={classes.strongText}>
                {source.product_name} – {legalDetails && legalDetails.tier} Tier
              </span>
            </Typography>
            <Typography className={classes.valueText}>
              Monthly Premium: {currencyCode}
              {quotation && quotation.total_premium_amount_in_cents
                ? (
                    quotation.total_premium_amount_in_cents / 100
                  ).toLocaleString()
                : 'N/A'}
            </Typography>
            <div>
              <Typography
                className={classes.strongText}
                style={{ marginTop: '10px' }}
              >
                Coverage Details:
              </Typography>
              <ul className={classes.scopeList}>
                <li>
                  Daily cash benefit for hospitalisation (minimum 72 hours)
                </li>
                <li>
                  Backdated payment to first 24 hours after 72-hour minimum
                </li>
                <li>Multiple tier options for different benefit levels</li>
              </ul>
            </div>
          </div>
        )}
      </Box>
    )
  }

  const buildMedicalMembersFromSchedule = (entries, globalTier) =>
    entries.map(entry => {
      const dep = entry.dependant || entry
      const relationship = dep.relationship || ''
      const benefits = entry.benefits || dep.benefits || []
      const illnessBenefit =
        benefits.find(b => b.type && b.type.includes('Illness')) ||
        benefits[0] ||
        {}
      const benefitAdd = illnessBenefit.additional_details || {}
      const dailyCoverCents =
        benefitAdd.daily_cover || illnessBenefit.cover_amount_in_cents || 0
      const premiumCents =
        entry.premium_amount_in_cents || dep.premium_amount_in_cents || 0
      const effectiveTier =
        entry.effective_tier || dep.effective_tier || globalTier || ''
      const isChild = CHILD_RELATIONSHIPS.includes(relationship.toLowerCase())
      return {
        memberName: `${dep.first_name || ''} ${dep.last_name || ''}`.trim(),
        relationship,
        package: `${effectiveTier} (${isChild ? 'Child' : 'Adult'})`,
        premium: premiumCents / 100,
        coverPerDay: dailyCoverCents / 100
      }
    })

  const renderCoveredMembers = () => {
    const scheduleEntries = quotation && quotation.policy_schedule_entries
    const globalTier =
      quotation &&
      quotation.additional_details &&
      quotation.additional_details.tier
    const mcpMembers =
      quotation &&
      quotation.additional_details &&
      quotation.additional_details.medicalCashPlan &&
      quotation.additional_details.medicalCashPlan.members
    // For medical policies: use medicalCashPlan if available, else fall back to schedule entries
    const medicalMembers = isMedicalPolicy
      ? mcpMembers ||
        (scheduleEntries &&
          buildMedicalMembersFromSchedule(scheduleEntries, globalTier))
      : null

    return (
      <Box className={classes.box}>
        <Typography className={classes.boxTitle}>Covered Members</Typography>
        {isMedicalPolicy && medicalMembers && medicalMembers.length > 0 ? (
          <div>
            {medicalMembers.map((member, index) => (
              <div key={index} className={classes.memberItem}>
                <Typography className={classes.valueText}>
                  <span className={classes.strongText}>Name:</span>{' '}
                  {member.memberName}
                </Typography>
                <Typography className={classes.valueText}>
                  <span className={classes.strongText}>Relationship:</span>{' '}
                  {member.relationship}
                </Typography>
                <Typography className={classes.valueText}>
                  <span className={classes.strongText}>Package:</span>{' '}
                  {member.package}
                </Typography>
                <Typography className={classes.valueText}>
                  <span className={classes.strongText}>Premium:</span>{' '}
                  {currencyCode}
                  {member.premium}
                </Typography>
                <Typography className={classes.valueText}>
                  <span className={classes.strongText}>Benefit:</span>{' '}
                  {currencyCode}
                  {member.coverPerDay
                    ? member.coverPerDay.toLocaleString()
                    : 'N/A'}
                  /day
                </Typography>
              </div>
            ))}
          </div>
        ) : isLegalPolicy && scheduleEntries ? (
          <div>
            {scheduleEntries.map((entry, index) => (
              <div key={index} className={classes.memberItem}>
                <Typography className={classes.valueText}>
                  <span className={classes.strongText}>Name:</span>{' '}
                  {entry.dependant
                    ? `${entry.dependant.first_name} ${entry.dependant.last_name}`
                    : 'N/A'}
                </Typography>
                <Typography className={classes.valueText}>
                  <span className={classes.strongText}>Relationship:</span>{' '}
                  {entry.dependant && entry.dependant.relationship
                    ? entry.dependant.relationship
                    : 'N/A'}
                </Typography>
                <div>
                  <Typography className={classes.valueText}>
                    <span className={classes.strongText}>Benefits:</span>
                  </Typography>
                  {entry.benefits && entry.benefits.length > 0 ? (
                    entry.benefits.map((benefit, benefitIndex) => (
                      <Typography
                        key={benefitIndex}
                        className={classes.valueText}
                        style={{ marginLeft: '20px' }}
                      >
                        {benefit.type}: {currencyCode}
                        {benefit.cover_amount_in_cents
                          ? (
                              benefit.cover_amount_in_cents / 100
                            ).toLocaleString()
                          : 'N/A'}
                      </Typography>
                    ))
                  ) : (
                    <Typography
                      className={classes.valueText}
                      style={{ marginLeft: '20px' }}
                    >
                      N/A
                    </Typography>
                  )}
                  <Typography className={classes.valueText}>
                    <span className={classes.strongText}>Premium:</span>{' '}
                    {currencyCode}
                    {entry.premium_amount_in_cents
                      ? (entry.premium_amount_in_cents / 100).toLocaleString()
                      : 'N/A'}
                  </Typography>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Typography className={classes.valueText}>
            No member information available
          </Typography>
        )}

        <div className={classes.premiumTotal}>
          <div className={classes.premiumRow}>
            <span className={classes.strongText}>Total Monthly Premium:</span>
            <span className={classes.strongText}>
              {currencyCode}
              {quotation && quotation.total_premium_amount_in_cents
                ? (
                    quotation.total_premium_amount_in_cents / 100
                  ).toLocaleString()
                : 'N/A'}
            </span>
          </div>
        </div>
      </Box>
    )
  }

  const renderBankDetails = () => {
    const bankDetails =
      source.additional_details && source.additional_details.bank_details
    if (!bankDetails) return null

    return (
      <Box className={classes.box}>
        <Typography className={classes.boxTitle}>Bank Details</Typography>
        <div className={classes.infoGrid}>
          <div>
            <Typography className={classes.valueText}>
              <span className={classes.strongText}>Bank:</span>{' '}
              {bankDetails.bank || 'N/A'}
            </Typography>
            <Typography className={classes.valueText}>
              <span className={classes.strongText}>Branch:</span>{' '}
              {bankDetails.bank_branch || 'N/A'}
            </Typography>
            <Typography className={classes.valueText}>
              <span className={classes.strongText}>Account Type:</span>{' '}
              {bankDetails.account_type || 'N/A'}
            </Typography>
          </div>
          <div>
            <Typography className={classes.valueText}>
              <span className={classes.strongText}>Account Number:</span>{' '}
              {bankDetails.account_number || 'N/A'}
            </Typography>
            <Typography className={classes.valueText}>
              <span className={classes.strongText}>Account Holder:</span>{' '}
              {bankDetails.account_holder_name || 'N/A'}
            </Typography>
            <Typography className={classes.valueText}>
              <span className={classes.strongText}>Debit Day:</span>{' '}
              {bankDetails.debit_day || 'N/A'}
            </Typography>
          </div>
        </div>
      </Box>
    )
  }

  const renderAdditionalDetails = () => {
    const details = source.additional_details || {}
    // Next of Kin Section
    const nextOfKin = [
      { label: 'Name', value: details.next_of_kin_name },
      { label: 'Relationship', value: details.next_of_kin_relationship },
      { label: 'National ID', value: details.next_of_kin_national_id },
      { label: 'Tel', value: details.next_of_kin_tel },
      { label: 'Email', value: details.next_of_kin_email },
      { label: 'Address', value: details.next_of_kin_address }
    ]
    // Employment Section
    const employment = [
      { label: 'Employer Name', value: details.employer_name },
      { label: 'Employer Tel', value: details.employer_tel },
      { label: 'Occupation', value: details.occupation },
      {
        label: 'Self Employed',
        value: details.is_self_employed ? 'Yes' : 'No'
      },
      { label: 'Business Name', value: details.self_employed_business_name },
      { label: 'Business Type', value: details.self_employed_business_type },
      {
        label: 'Business Contact',
        value: details.self_employed_business_contact
      }
    ]
    // Standalone fields
    const paymentMethod =
      details.payment_method || source.payment_method || 'N/A'
    const agentBroker = details.agent_broker || 'N/A'
    const createdBy = details.created_by || 'N/A'
    const onboardingSource = details.onboarding_source || 'N/A'
    const pip =
      details.is_pip === true ? 'Yes' : details.is_pip === false ? 'No' : 'None'
    const pipLabel = 'Politically Influential Person'
    const pipDetails = details.pip_details || 'N/A'
    const createdAt = source.created_at
      ? formatDateLocale(source.created_at)
      : 'N/A'

    return (
      <>
        {/* Next of Kin Section */}
        <Box className={classes.box}>
          <Typography className={classes.boxTitle}>
            Next of Kin Details
          </Typography>
          {nextOfKin.map((item, idx) => (
            <Typography key={idx} className={classes.valueText}>
              <span className={classes.strongText}>{item.label}:</span>{' '}
              {item.value || 'N/A'}
            </Typography>
          ))}
        </Box>
        {/* Employment Section */}
        <Box className={classes.box}>
          <Typography className={classes.boxTitle}>
            Employment Details
          </Typography>
          {employment.map((item, idx) => (
            <Typography key={idx} className={classes.valueText}>
              <span className={classes.strongText}>{item.label}:</span>{' '}
              {item.value || 'N/A'}
            </Typography>
          ))}
        </Box>
        {/* Standalone fields */}
        <Box className={classes.box}>
          <Typography className={classes.boxTitle}>Other Details</Typography>
          <Typography className={classes.valueText}>
            <span className={classes.strongText}>
              Requested Policy Start Day:
            </span>{' '}
            {details.policy_start_date || 'N/A'}
          </Typography>
          <Typography className={classes.valueText}>
            <span className={classes.strongText}>Payment Method:</span>{' '}
            {paymentMethod}
          </Typography>

          <Typography className={classes.valueText}>
            <span className={classes.strongText}>{pipLabel}:</span> {pip}
          </Typography>
          {pip === 'Yes' && (
            <Typography className={classes.valueText}>
              <span className={classes.strongText}>PIP Details:</span>{' '}
              {pipDetails}
            </Typography>
          )}
          <Typography className={classes.valueText}>
            <span className={classes.strongText}>Country of Birth:</span>{' '}
            {details.country_of_birth || 'N/A'}
          </Typography>
          <br />
          <Typography className={classes.valueText}>
            <span className={classes.strongText}>Agent/Broker:</span>{' '}
            {agentBroker}
          </Typography>
          <Typography className={classes.valueText}>
            <span className={classes.strongText}>Onboarding Source:</span>{' '}
            {onboardingSource}
          </Typography>
          <Typography className={classes.valueText}>
            <span className={classes.strongText}>Created By:</span> {createdBy}
          </Typography>
          <Typography className={classes.valueText}>
            <span className={classes.strongText}>Created At:</span> {createdAt}
          </Typography>
        </Box>
      </>
    )
  }

  const renderCommercialCover = () => {
    const details = source.additional_details || {}
    const commercialProducts =
      (quotation &&
        quotation.additional_details &&
        quotation.additional_details.commercial_products) ||
      []

    return (
      <Box className={classes.box}>
        <Typography className={classes.boxTitle}>
          Commercial Cover Details
        </Typography>
        <div>
          <Typography className={classes.valueText}>
            <span className={classes.strongText}>Company Name:</span>{' '}
            {details.company_name || 'N/A'}
          </Typography>
          <Typography className={classes.valueText}>
            <span className={classes.strongText}>Company Email:</span>{' '}
            {details.company_email || 'N/A'}
          </Typography>
          <Typography className={classes.valueText}>
            <span className={classes.strongText}>Company Phone:</span>{' '}
            {details.company_phone || 'N/A'}
          </Typography>
          <Typography className={classes.valueText}>
            <span className={classes.strongText}>Risk Location:</span>{' '}
            {details.company_risk_location || 'N/A'}
          </Typography>
          <Typography className={classes.valueText}>
            <span className={classes.strongText}>Postal Address:</span>{' '}
            {details.company_postal_address || 'N/A'}
          </Typography>
          <Typography className={classes.valueText}>
            <span className={classes.strongText}>Registration Number:</span>{' '}
            {details.company_registration_number || 'N/A'}
          </Typography>
          <Typography className={classes.valueText}>
            <span className={classes.strongText}>Payment Plan:</span>{' '}
            {details.payment_plan || 'N/A'}
          </Typography>
          <Typography className={classes.valueText}>
            <span className={classes.strongText}>Payment Method:</span>{' '}
            {source.payment_method || 'N/A'}
          </Typography>
          <Typography className={classes.valueText}>
            <span className={classes.strongText}>Debit Order Date:</span>{' '}
            {details.debit_order_date || 'N/A'}
          </Typography>
          <Typography className={classes.valueText}>
            <span className={classes.strongText}>Debit Order Authorized:</span>{' '}
            {details.debit_order_authorized ? 'Yes' : 'No'}
          </Typography>
          <Typography className={classes.valueText}>
            <span className={classes.strongText}>Application Type:</span>{' '}
            {details.application_type || 'N/A'}
          </Typography>
          <Typography className={classes.valueText}>
            <span className={classes.strongText}>Agent/Broker:</span>{' '}
            {details.agent_broker || 'N/A'}
          </Typography>
          <Typography className={classes.valueText}>
            <span className={classes.strongText}>Source:</span>{' '}
            {details.source || 'N/A'}
          </Typography>
          <Typography className={classes.valueText}>
            <span className={classes.strongText}>Created By:</span>{' '}
            {details.created_by || 'N/A'}
          </Typography>
          <Typography className={classes.valueText}>
            <span className={classes.strongText}>Designation:</span>{' '}
            {details.designation || 'N/A'}
          </Typography>
          <Typography className={classes.valueText}>
            <span className={classes.strongText}>Policy Start Date:</span>{' '}
            {source.start_date ? formatDateLocale(source.start_date) : 'N/A'}
          </Typography>
          <Typography className={classes.valueText}>
            <span className={classes.strongText}>Policy End Date:</span>{' '}
            {details.policy_end_date
              ? formatDateLocale(details.policy_end_date)
              : 'N/A'}
          </Typography>
        </div>

        <Typography className={classes.boxTitle} style={{ marginTop: 20 }}>
          Insured Items
        </Typography>
        {commercialProducts.length > 0 ? (
          commercialProducts.map((item, idx) => (
            <div key={idx} className={classes.memberItem}>
              <Typography className={classes.valueText}>
                <span className={classes.strongText}>Item:</span>{' '}
                {item.insured_item || 'N/A'}
              </Typography>
              <Typography className={classes.valueText}>
                <span className={classes.strongText}>Insurance Type:</span>{' '}
                {item.insurance_type || 'N/A'}
              </Typography>
              <Typography className={classes.valueText}>
                <span className={classes.strongText}>Sum Insured:</span>{' '}
                {currencyCode}
                {item.sum_insured ? item.sum_insured.toLocaleString() : 'N/A'}
              </Typography>
              <Typography className={classes.valueText}>
                <span className={classes.strongText}>Annual Premium:</span>{' '}
                {currencyCode}
                {item.annual_premium
                  ? item.annual_premium.toLocaleString()
                  : 'N/A'}
              </Typography>
              <Typography className={classes.valueText}>
                <span className={classes.strongText}>Monthly Premium:</span>{' '}
                {currencyCode}
                {item.monthly_premium
                  ? item.monthly_premium.toLocaleString()
                  : 'N/A'}
              </Typography>
              <Typography className={classes.valueText}>
                <span className={classes.strongText}>Rate Percentage:</span>{' '}
                {item.rate_percentage || 'N/A'}%
              </Typography>
              <Typography className={classes.valueText}>
                <span className={classes.strongText}>Description:</span>{' '}
                {item.additional_details
                  ? item.additional_details.commercial_insured_description ||
                    'N/A'
                  : 'N/A'}
              </Typography>
              <Typography className={classes.valueText}>
                <span className={classes.strongText}>Notes:</span>{' '}
                {item.additional_details
                  ? item.additional_details.additional_notes || 'N/A'
                  : 'N/A'}
              </Typography>
            </div>
          ))
        ) : (
          <Typography className={classes.valueText}>
            No insured items found.
          </Typography>
        )}

        <div className={classes.premiumTotal}>
          <div className={classes.premiumRow}>
            <span className={classes.strongText}>Total Premium:</span>
            <span className={classes.strongText}>
              {currencyCode}
              {quotation && quotation.total_premium_amount_in_cents
                ? (
                    quotation.total_premium_amount_in_cents / 100
                  ).toLocaleString()
                : 'N/A'}
            </span>
          </div>
        </div>
      </Box>
    )
  }

  const renderDomesticCover = () => {
    const details = source.additional_details || {}
    const domesticProducts =
      (quotation &&
        quotation.additional_details &&
        quotation.additional_details.domestic_products) ||
      details.domestic_products ||
      []

    const principalEntry = getPrincipalMember(quotation)
    const principal = (principalEntry && principalEntry.dependant) || {}
    const principalDetails = principal.additional_details || {}
    const contactName = `${principal.first_name || ''} ${principal.last_name ||
      ''}`.trim()
    const contactEmail = principalDetails.email || details.company_email
    const contactPhone = principal.msisdn || details.company_phone

    return (
      <Box className={classes.box}>
        <Typography className={classes.boxTitle}>
          Domestic Cover Details
        </Typography>
        <div>
          <Typography className={classes.valueText}>
            <span className={classes.strongText}>Contact Name:</span>{' '}
            {contactName || 'N/A'}
          </Typography>
          <Typography className={classes.valueText}>
            <span className={classes.strongText}>Contact Email:</span>{' '}
            {contactEmail || 'N/A'}
          </Typography>
          <Typography className={classes.valueText}>
            <span className={classes.strongText}>Contact Phone:</span>{' '}
            {contactPhone || 'N/A'}
          </Typography>
          <Typography className={classes.valueText}>
            <span className={classes.strongText}>Risk Location:</span>{' '}
            {details.company_risk_location || 'N/A'}
          </Typography>
          <Typography className={classes.valueText}>
            <span className={classes.strongText}>Postal Address:</span>{' '}
            {details.company_postal_address || 'N/A'}
          </Typography>
          <Typography className={classes.valueText}>
            <span className={classes.strongText}>Registration Number:</span>{' '}
            {details.company_registration_number || 'N/A'}
          </Typography>
          <Typography className={classes.valueText}>
            <span className={classes.strongText}>Payment Plan:</span>{' '}
            {details.payment_plan || 'N/A'}
          </Typography>
          <Typography className={classes.valueText}>
            <span className={classes.strongText}>Payment Method:</span>{' '}
            {source.payment_method || 'N/A'}
          </Typography>
          <Typography className={classes.valueText}>
            <span className={classes.strongText}>Debit Order Date:</span>{' '}
            {details.debit_order_date || 'N/A'}
          </Typography>
          <Typography className={classes.valueText}>
            <span className={classes.strongText}>Debit Order Authorized:</span>{' '}
            {details.debit_order_authorized ? 'Yes' : 'No'}
          </Typography>
          <Typography className={classes.valueText}>
            <span className={classes.strongText}>Application Type:</span>{' '}
            {details.application_type || 'N/A'}
          </Typography>
          <Typography className={classes.valueText}>
            <span className={classes.strongText}>Agent/Broker:</span>{' '}
            {details.agent_broker || 'N/A'}
          </Typography>
          <Typography className={classes.valueText}>
            <span className={classes.strongText}>Source:</span>{' '}
            {details.source || 'N/A'}
          </Typography>
          <Typography className={classes.valueText}>
            <span className={classes.strongText}>Created By:</span>{' '}
            {details.created_by || 'N/A'}
          </Typography>
          <Typography className={classes.valueText}>
            <span className={classes.strongText}>Designation:</span>{' '}
            {details.designation || 'N/A'}
          </Typography>
          <Typography className={classes.valueText}>
            <span className={classes.strongText}>Policy Start Date:</span>{' '}
            {source.start_date ? formatDateLocale(source.start_date) : 'N/A'}
          </Typography>
          <Typography className={classes.valueText}>
            <span className={classes.strongText}>Policy End Date:</span>{' '}
            {details.policy_end_date
              ? formatDateLocale(details.policy_end_date)
              : 'N/A'}
          </Typography>
        </div>

        <Typography className={classes.boxTitle} style={{ marginTop: 20 }}>
          Insured Items
        </Typography>
        {domesticProducts.length > 0 ? (
          domesticProducts.map((item, idx) => {
            const itemDetails = item.additional_details || {}
            return (
              <div key={idx} className={classes.memberItem}>
                <Typography className={classes.valueText}>
                  <span className={classes.strongText}>Item:</span>{' '}
                  {item.insured_item || 'N/A'}
                </Typography>
                <Typography className={classes.valueText}>
                  <span className={classes.strongText}>Product Type:</span>{' '}
                  {item.product_type || 'N/A'}
                </Typography>
                <Typography className={classes.valueText}>
                  <span className={classes.strongText}>Cover Type:</span>{' '}
                  {itemDetails.cover_type || 'N/A'}
                </Typography>
                <Typography className={classes.valueText}>
                  <span className={classes.strongText}>Sum Insured:</span>{' '}
                  {currencyCode}
                  {item.sum_insured ? item.sum_insured.toLocaleString() : 'N/A'}
                </Typography>
                <Typography className={classes.valueText}>
                  <span className={classes.strongText}>Annual Premium:</span>{' '}
                  {currencyCode}
                  {item.annual_premium
                    ? item.annual_premium.toLocaleString()
                    : 'N/A'}
                </Typography>
                <Typography className={classes.valueText}>
                  <span className={classes.strongText}>Monthly Premium:</span>{' '}
                  {currencyCode}
                  {item.monthly_premium
                    ? item.monthly_premium.toLocaleString()
                    : 'N/A'}
                </Typography>
                <Typography className={classes.valueText}>
                  <span className={classes.strongText}>Rate Percentage:</span>{' '}
                  {item.rate_percentage || 'N/A'}%
                </Typography>
                <Typography className={classes.valueText}>
                  <span className={classes.strongText}>Make:</span>{' '}
                  {itemDetails.make || 'N/A'}
                </Typography>
                <Typography className={classes.valueText}>
                  <span className={classes.strongText}>Model:</span>{' '}
                  {itemDetails.model || 'N/A'}
                </Typography>
                <Typography className={classes.valueText}>
                  <span className={classes.strongText}>Color:</span>{' '}
                  {itemDetails.color || 'N/A'}
                </Typography>
                <Typography className={classes.valueText}>
                  <span className={classes.strongText}>
                    Year of Manufacture:
                  </span>{' '}
                  {itemDetails.year_of_manufacture || 'N/A'}
                </Typography>
                <Typography className={classes.valueText}>
                  <span className={classes.strongText}>
                    Registration Number:
                  </span>{' '}
                  {itemDetails.registration_number || 'N/A'}
                </Typography>
                <Typography className={classes.valueText}>
                  <span className={classes.strongText}>Engine Number:</span>{' '}
                  {itemDetails.engine_number || 'N/A'}
                </Typography>
                <Typography className={classes.valueText}>
                  <span className={classes.strongText}>Chassis Number:</span>{' '}
                  {itemDetails.chassis_number || 'N/A'}
                </Typography>
                <Typography className={classes.valueText}>
                  <span className={classes.strongText}>Risk Location:</span>{' '}
                  {itemDetails.risk_location || 'N/A'}
                </Typography>
                <Typography className={classes.valueText}>
                  <span className={classes.strongText}>Risk Post Code:</span>{' '}
                  {itemDetails.risk_post_code || 'N/A'}
                </Typography>
                <Typography className={classes.valueText}>
                  <span className={classes.strongText}>Notes:</span>{' '}
                  {itemDetails.additional_notes || 'N/A'}
                </Typography>
              </div>
            )
          })
        ) : (
          <Typography className={classes.valueText}>
            No insured items found.
          </Typography>
        )}

        <div className={classes.premiumTotal}>
          <div className={classes.premiumRow}>
            <span className={classes.strongText}>Total Premium:</span>
            <span className={classes.strongText}>
              {currencyCode}
              {quotation && quotation.total_premium_amount_in_cents
                ? (
                    quotation.total_premium_amount_in_cents / 100
                  ).toLocaleString()
                : 'N/A'}
            </span>
          </div>
        </div>
      </Box>
    )
  }

  if (source.product_code === 'commercial_cover') {
    return renderCommercialCover()
  }
  if (isDomesticPolicy) {
    return renderDomesticCover()
  }
  return (
    <>
      {renderSelectedProduct()}
      {renderCoveredMembers()}
      {renderBankDetails()}
      {renderAdditionalDetails()}
    </>
  )
}
